import { Link, Outlet } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { PageBar } from '@/app/layout/page-bar'
import { cn } from '@/shared/lib/utils'

/**
 * The frame every administration page renders inside.
 *
 * A strip of links rather than a second sidebar. Upstream replaces the
 * workspace sidebar's contents while an admin page is open, which loses the way
 * back to the work; here the sidebar keeps its meaning and the sections are
 * tabs across the top of the area they belong to.
 *
 * The guard is on the route rather than here -- see `requireStaff`. A component
 * that decided what to render would still have fetched nothing and shown a
 * blank page to somebody who typed the url, which is not the same as refusing.
 */
export function AdminShell() {
  const { t } = useTranslation()

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageBar>
        <nav aria-label={t('admin.title')} className="flex min-w-0 gap-1 overflow-x-auto">
          <AdminLink to="/admin/users">{t('admin.users')}</AdminLink>
          <AdminLink to="/admin/settings">{t('admin.settings')}</AdminLink>
          <AdminLink to="/admin/health">{t('admin.health')}</AdminLink>
        </nav>
      </PageBar>

      <div className="min-h-0 flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  )
}

/**
 * One section of the administration area.
 *
 * A real link, so the section is bookmarkable and the back button works, and
 * `aria-current` is the router's to write -- it knows which one is active and a
 * hand-written comparison would be a second rule to keep in step.
 */
function AdminLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className={cn(
        'rounded-md px-3 py-1.5 text-sm whitespace-nowrap transition-colors',
        'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
      activeProps={{ className: 'bg-accent text-foreground font-medium' }}
    >
      {children}
    </Link>
  )
}
